/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */
package studio

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.Month
import kotlinx.datetime.plus
import kotlin.math.pow

/**
 * Month-by-month projection of a vehicle's balance, tax events, and final
 * net-to-user value. See §4.2.
 *
 * Phase 1 ships TFSA, RRSP, and FHSA. RESP, RDSP, non-registered land in
 * Phase 2 (§9). The function refuses (returns `eligible = false`) for any
 * vehicle outside this set so the UI can render a skeleton without crashing.
 *
 * Pure module — no I/O, no platform calls.
 */
object VehicleProjector {

    /** Phase 1 vehicles. NON_REGISTERED is included so that capped-vehicle
     *  overflow can spill into a taxable account. */
    private val PHASE_1_VEHICLES = setOf(
        VehicleType.TFSA, VehicleType.RRSP, VehicleType.FHSA,
        VehicleType.HISA, VehicleType.NON_REGISTERED,
    )

    fun project(inputs: ProjectionInputs): VehicleProjection {
        if (inputs.vehicle !in PHASE_1_VEHICLES) return ineligible(inputs, "Vehicle not supported in Phase 1")
        if (inputs.taxContext.region != Region.CA) return ineligible(inputs, "Studio requires CA region")

        val eligibilityIssue = checkEligibility(inputs)
        if (eligibilityIssue != null) return ineligible(inputs, eligibilityIssue)

        return when (inputs.vehicle) {
            VehicleType.TFSA -> projectTfsa(inputs)
            VehicleType.RRSP -> projectRrsp(inputs)
            VehicleType.FHSA -> projectFhsa(inputs)
            VehicleType.HISA -> projectHisa(inputs)
            VehicleType.NON_REGISTERED -> projectNonRegistered(inputs)
            else -> ineligible(inputs, "Vehicle not supported in Phase 1")
        }
    }

    // -----------------------------------------------------------------------
    // Eligibility
    // -----------------------------------------------------------------------

    private fun checkEligibility(inputs: ProjectionInputs): String? = when (inputs.vehicle) {
        VehicleType.FHSA -> if (!inputs.taxContext.isFirstTimeHomeBuyer)
            "FHSA requires the user to be a first-time home buyer" else null
        else -> null
    }

    // -----------------------------------------------------------------------
    // TFSA
    // -----------------------------------------------------------------------

    private fun projectTfsa(inputs: ProjectionInputs): VehicleProjection {
        val monthlyReturn = monthlyFromAnnualReturn(inputs.expectedAnnualReturn)
        val roomKnown = !inputs.contributionRoom.tfsaUnknown
        val requested = inputs.monthlyContribution.coerceAtLeast(0.0)
        var roomRemaining = if (!roomKnown) 0.0 else inputs.contributionRoom.tfsa.coerceAtLeast(0.0)
        var currentYear = inputs.today.year

        val points = mutableListOf<VehiclePoint>()
        var balance = 0.0
        var totalContributions = 0.0
        for (month in 1..inputs.monthsToGoal) {
            val date = inputs.today.plus(DatePeriod(months = month))
            // Annual room accrual at the calendar-year boundary.
            if (date.year > currentYear) {
                if (roomKnown) roomRemaining += annualRoomAccrual(VehicleType.TFSA, date.year, inputs.taxContext)
                currentYear = date.year
            }
            val actual = if (!roomKnown) 0.0
                else minOf(requested, roomRemaining).coerceAtLeast(0.0)
            roomRemaining = (roomRemaining - actual).coerceAtLeast(0.0)
            totalContributions += actual
            balance = (balance + actual) * (1.0 + monthlyReturn)
            points += VehiclePoint(
                date = date,
                grossBalance = balance,
                totalWealth = balance,
                netToUser = balance,
                roomRemaining = roomRemaining,
                monthlyContribution = actual,
            )
        }
        val averageContribution = if (inputs.monthsToGoal > 0) totalContributions / inputs.monthsToGoal else 0.0
        return VehicleProjection(
            vehicle = VehicleType.TFSA,
            eligible = true,
            recommendation = Recommendation.BACKUP,
            recommendationReason = "TFSA grows tax-free with full withdrawal flexibility.",
            points = points,
            events = emptyList(),
            finalNetToUser = points.lastOrNull()?.netToUser ?: 0.0,
            effectiveSavingsRateContribution = SavingsRateBreakdownCalculator.compute(
                monthlyContribution = averageContribution,
                vehicle = VehicleType.TFSA,
                taxContext = inputs.taxContext,
            ),
        )
    }

    // -----------------------------------------------------------------------
    // RRSP
    // -----------------------------------------------------------------------

    private fun projectRrsp(inputs: ProjectionInputs): VehicleProjection {
        val monthlyReturn = monthlyFromAnnualReturn(inputs.expectedAnnualReturn)
        val roomKnown = !inputs.contributionRoom.rrspUnknown
        val requested = inputs.monthlyContribution.coerceAtLeast(0.0)
        val marginal = inputs.taxContext.marginalRateNow
        var roomRemaining = if (!roomKnown) 0.0 else inputs.contributionRoom.rrsp.coerceAtLeast(0.0)
        var currentYear = inputs.today.year

        val points = mutableListOf<VehiclePoint>()
        val events = mutableListOf<VehicleEvent>()
        var balance = 0.0
        var cumulativeRefunds = 0.0
        var totalContributions = 0.0
        val yearlyContributionAccumulator = mutableMapOf<Int, Double>()
        val refundedYears = mutableSetOf<Int>()

        for (month in 1..inputs.monthsToGoal) {
            val date = inputs.today.plus(DatePeriod(months = month))
            // Annual room accrual at calendar-year boundary.
            if (date.year > currentYear) {
                if (roomKnown) roomRemaining += annualRoomAccrual(VehicleType.RRSP, date.year, inputs.taxContext)
                currentYear = date.year
            }
            val actual = if (!roomKnown) 0.0
                else minOf(requested, roomRemaining).coerceAtLeast(0.0)
            roomRemaining = (roomRemaining - actual).coerceAtLeast(0.0)
            totalContributions += actual
            balance = (balance + actual) * (1.0 + monthlyReturn)
            yearlyContributionAccumulator[date.year] =
                (yearlyContributionAccumulator[date.year] ?: 0.0) + actual

            // Refund hits each May for the prior year's contributions. Day-of-month
            // floats with `today`, so trigger on any May date and dedupe per year.
            if (date.month >= Month.MAY && (date.year - 1) !in refundedYears) {
                val priorYearContribs = yearlyContributionAccumulator[date.year - 1] ?: 0.0
                if (priorYearContribs > 0) {
                    val refund = priorYearContribs * marginal
                    cumulativeRefunds += refund
                    refundedYears += (date.year - 1)
                    events += VehicleEvent(
                        date = LocalDate(date.year, Month.MAY, 1),
                        type = VehicleEventType.REFUND,
                        amount = refund,
                        label = "+\$${formatMoneyShort(refund)} tax refund",
                        explanation =
                            "Prior-year RRSP contributions of \$${formatMoneyShort(priorYearContribs)} × " +
                                    "${formatPercent(marginal)} marginal rate = \$${formatMoneyShort(refund)} refund.",
                    )
                }
            }
            points += VehiclePoint(
                date = date,
                grossBalance = balance,
                totalWealth = balance + cumulativeRefunds,
                netToUser = (balance + cumulativeRefunds) - balance * inputs.futureMarginalRate,
                roomRemaining = roomRemaining,
                monthlyContribution = actual,
            )
        }

        // Pending refunds: contribution-years whose May refund falls after the
        // goal date haven't been "paid out" by the time the user wants to look
        // at their wealth — but they're entitled to them. Include them in
        // netToUser so a short-term goal (e.g. 12 months ending in January)
        // doesn't display RRSP as worse than TFSA just because the refund
        // hasn't fired yet. Emits a single REFUND event at the goal date so
        // PR4's growth chart can render the bump.
        val pendingRefund = computePendingRefund(yearlyContributionAccumulator, points.last().date, marginal)
        if (pendingRefund > 0) {
            events += VehicleEvent(
                date = points.last().date,
                type = VehicleEventType.REFUND,
                amount = pendingRefund,
                label = "+\$${formatMoneyShort(pendingRefund)} expected refund",
                explanation =
                    "Refunds arrive in May of the year after each contribution year. Included here so the goal-date net" +
                            " reflects what you're entitled to, not just what's already in your account.",
            )
        }

        // Tax-owed-at-withdrawal event at the goal date.
        val finalGross = points.lastOrNull()?.grossBalance ?: 0.0
        val taxOwed = finalGross * inputs.futureMarginalRate
        if (taxOwed > 0) {
            events += VehicleEvent(
                date = points.last().date,
                type = VehicleEventType.TAX_OWED,
                amount = -taxOwed,
                label = "−\$${formatMoneyShort(taxOwed)} tax if withdrawn that day",
                explanation =
                    "If you withdrew the full RRSP balance on this date: " +
                            "\$${formatMoneyShort(finalGross)} × " +
                            "${formatPercent(inputs.futureMarginalRate)} future marginal rate = " +
                            "\$${formatMoneyShort(taxOwed)} tax owed. In practice you'd draw it down across " +
                            "many retirement years at varying rates, often paying less overall.",
            )
        }

        val finalNet = (points.lastOrNull()?.netToUser ?: 0.0) + pendingRefund
        val averageContribution = if (inputs.monthsToGoal > 0) totalContributions / inputs.monthsToGoal else 0.0

        return VehicleProjection(
            vehicle = VehicleType.RRSP,
            eligible = true,
            recommendation = Recommendation.BACKUP,
            recommendationReason = "RRSP defers tax now; what you owe at withdrawal depends on your future rate.",
            points = points,
            events = events,
            finalNetToUser = finalNet,
            effectiveSavingsRateContribution = SavingsRateBreakdownCalculator.compute(
                monthlyContribution = averageContribution,
                vehicle = VehicleType.RRSP,
                taxContext = inputs.taxContext,
            ),
        )
    }

    // -----------------------------------------------------------------------
    // HISA — educational "no growth, just liquid" stub. Ignores the user's
    // expectedAnnualReturn slider (HISA rates are typically ≤ inflation, so
    // we model the conservative case: 0% real return). The Studio shows
    // HISA next to invested vehicles to make the "sleeping money" cost
    // visible at a glance.
    // -----------------------------------------------------------------------

    private fun projectHisa(inputs: ProjectionInputs): VehicleProjection {
        val monthly = inputs.monthlyContribution.coerceAtLeast(0.0)
        val points = mutableListOf<VehiclePoint>()
        var balance = 0.0
        for (month in 1..inputs.monthsToGoal) {
            balance += monthly
            val date = inputs.today.plus(DatePeriod(months = month))
            points += VehiclePoint(date = date, grossBalance = balance, totalWealth = balance, netToUser = balance)
        }
        return VehicleProjection(
            vehicle = VehicleType.HISA,
            eligible = true,
            recommendation = Recommendation.BACKUP,  // recomputed in comparator
            recommendationReason = "Cash savings — liquid, but no real growth (inflation slowly erodes purchasing power).",
            points = points,
            events = emptyList(),
            finalNetToUser = points.lastOrNull()?.netToUser ?: 0.0,
            effectiveSavingsRateContribution = SavingsRateBreakdownCalculator.compute(
                monthlyContribution = monthly,
                vehicle = VehicleType.HISA,
                taxContext = inputs.taxContext,
            ),
        )
    }

    // -----------------------------------------------------------------------
    // NON_REGISTERED — taxable open account. Modeled with two tax effects:
    //   (a) Dividend drag — assume ~30% of the user's expected total
    //       return comes as dividends, taxed annually at marginal rate.
    //       This reduces the effective compounding rate.
    //   (b) Capital-gains tax at sale — 50% of accumulated gains (the
    //       Canadian inclusion rate) taxed at marginal rate. Applied as a
    //       drop at goal date.
    //
    // Heuristic assumption: 30% of total return is dividends. This is a
    // rough average for a Canadian balanced portfolio; refinement could
    // factor in eligible-Canadian dividends (which get the dividend tax
    // credit) vs foreign / interest (fully taxed). Phase 2 can sharpen.
    // -----------------------------------------------------------------------

    private fun projectNonRegistered(inputs: ProjectionInputs): VehicleProjection {
        val totalReturn = inputs.expectedAnnualReturn
        val marginal = inputs.taxContext.marginalRateNow
        val divFraction = 0.30
        // Dividend drag: divs × marginal rate. e.g. 7.5% × 30% × 30% = 0.675%/yr.
        val divDrag = totalReturn * divFraction * marginal
        val effectiveReturn = (totalReturn - divDrag).coerceAtLeast(0.0)
        val monthlyReturn = monthlyFromAnnualReturn(effectiveReturn)
        val monthly = inputs.monthlyContribution.coerceAtLeast(0.0)

        val points = mutableListOf<VehiclePoint>()
        val events = mutableListOf<VehicleEvent>()
        var balance = 0.0
        var contributions = 0.0
        for (month in 1..inputs.monthsToGoal) {
            balance = (balance + monthly) * (1.0 + monthlyReturn)
            contributions += monthly
            val date = inputs.today.plus(DatePeriod(months = month))
            val capGains = (balance - contributions).coerceAtLeast(0.0)
            val capGainsTax = capGains * 0.5 * marginal
            points += VehiclePoint(
                date = date,
                grossBalance = balance,
                totalWealth = balance,
                netToUser = balance - capGainsTax,
            )
        }
        // Emit the cap-gains TAX_OWED event so the chart drop has a hover
        // explanation (matches the RRSP withdrawal-tax pattern).
        val finalBalance = points.lastOrNull()?.grossBalance ?: 0.0
        val finalContribs = inputs.monthsToGoal * monthly
        val finalCapGains = (finalBalance - finalContribs).coerceAtLeast(0.0)
        val finalCapGainsTax = finalCapGains * 0.5 * marginal
        if (finalCapGainsTax > 0.0) {
            events += VehicleEvent(
                date = points.last().date,
                type = VehicleEventType.TAX_OWED,
                amount = -finalCapGainsTax,
                label = "−\$${formatMoneyShort(finalCapGainsTax)} cap gains if sold",
                explanation =
                    "Non-registered gains are taxed at the 50% inclusion rate × your marginal rate. " +
                        "\$${formatMoneyShort(finalCapGains)} of gains × ${formatPercent(0.5 * marginal)} = " +
                        "\$${formatMoneyShort(finalCapGainsTax)} tax owed if you sold on this date. " +
                        "Dividends are also taxed annually — assumed ${formatPercent(divFraction)} of returns are " +
                        "dividends, costing ~${formatPercent(divDrag)}/yr of effective growth."
            )
        }
        return VehicleProjection(
            vehicle = VehicleType.NON_REGISTERED,
            eligible = true,
            recommendation = Recommendation.BACKUP,
            recommendationReason = "Non-registered is taxable: dividends each year, plus capital gains at sale.",
            points = points,
            events = events,
            finalNetToUser = points.lastOrNull()?.netToUser ?: 0.0,
            effectiveSavingsRateContribution = SavingsRateBreakdownCalculator.compute(
                monthlyContribution = monthly,
                vehicle = VehicleType.NON_REGISTERED,
                taxContext = inputs.taxContext,
            ),
        )
    }

    // -----------------------------------------------------------------------
    // FHSA
    // -----------------------------------------------------------------------

    private fun projectFhsa(inputs: ProjectionInputs): VehicleProjection {
        val monthlyReturn = monthlyFromAnnualReturn(inputs.expectedAnnualReturn)
        val marginal = inputs.taxContext.marginalRateNow
        val requested = inputs.monthlyContribution.coerceAtLeast(0.0)
        // FHSA dynamics: $40K lifetime cap, $8K accrues each Jan 1 (capped
        // by lifetime remaining). Start with min(annual, lifetime) of room.
        var lifetimeRemaining = inputs.contributionRoom.fhsa.remaining.coerceAtLeast(0.0)
        var roomRemaining = minOf(annualRoomAccrual(VehicleType.FHSA, inputs.today.year, inputs.taxContext), lifetimeRemaining)
        var currentYear = inputs.today.year

        val points = mutableListOf<VehiclePoint>()
        val events = mutableListOf<VehicleEvent>()
        var balance = 0.0
        var cumulativeRefunds = 0.0
        var totalContributions = 0.0
        val yearlyContributionAccumulator = mutableMapOf<Int, Double>()
        val refundedYears = mutableSetOf<Int>()

        for (month in 1..inputs.monthsToGoal) {
            val date = inputs.today.plus(DatePeriod(months = month))
            // Annual room accrual at calendar-year boundary, bounded by
            // lifetime cap (a Jan 1 of year 5 brings 0 new room if the
            // user has already contributed $40K).
            if (date.year > currentYear) {
                val newAccrual = minOf(annualRoomAccrual(VehicleType.FHSA, date.year, inputs.taxContext), lifetimeRemaining)
                roomRemaining += newAccrual
                currentYear = date.year
            }
            val actual = minOf(requested, roomRemaining).coerceAtLeast(0.0)
            roomRemaining = (roomRemaining - actual).coerceAtLeast(0.0)
            lifetimeRemaining = (lifetimeRemaining - actual).coerceAtLeast(0.0)
            totalContributions += actual
            balance = (balance + actual) * (1.0 + monthlyReturn)
            yearlyContributionAccumulator[date.year] =
                (yearlyContributionAccumulator[date.year] ?: 0.0) + actual

            if (date.month >= Month.MAY && (date.year - 1) !in refundedYears) {
                val priorYearContribs = yearlyContributionAccumulator[date.year - 1] ?: 0.0
                if (priorYearContribs > 0) {
                    val refund = priorYearContribs * marginal
                    cumulativeRefunds += refund
                    refundedYears += (date.year - 1)
                    events += VehicleEvent(
                        date = LocalDate(date.year, Month.MAY, 1),
                        type = VehicleEventType.REFUND,
                        amount = refund,
                        label = "+\$${formatMoneyShort(refund)} tax refund",
                        explanation =
                            "Prior-year FHSA contributions of \$${formatMoneyShort(priorYearContribs)} × " +
                                    "${formatPercent(marginal)} marginal rate = \$${formatMoneyShort(refund)} refund.",
                    )
                }
            }

            // Expiry warning at 15-year anchor.
            if (month == inputs.contributionRoom.fhsa.yearsUntilExpiry * 12) {
                events += VehicleEvent(
                    date = date,
                    type = VehicleEventType.EXPIRY_WARNING,
                    amount = 0.0,
                    label = "FHSA expires soon",
                    explanation =
                        "FHSA must be used (qualifying-home withdrawal) or rolled into an RRSP within 15 years " +
                                "of the first contribution. Unused funds become taxable income otherwise.",
                )
            }

            // FHSA qualifying-home withdrawal: no tax event. netToUser = totalWealth.
            points += VehiclePoint(
                date = date,
                grossBalance = balance,
                totalWealth = balance + cumulativeRefunds,
                netToUser = balance + cumulativeRefunds,
                roomRemaining = roomRemaining,
                monthlyContribution = actual,
            )
        }

        // Same pending-refund treatment as RRSP — refunds for contribution years
        // whose May payout falls after the goal date are still owed to the user.
        val pendingRefund = computePendingRefund(yearlyContributionAccumulator, points.last().date, marginal)
        if (pendingRefund > 0) {
            events += VehicleEvent(
                date = points.last().date,
                type = VehicleEventType.REFUND,
                amount = pendingRefund,
                label = "+\$${formatMoneyShort(pendingRefund)} expected refund",
                explanation =
                    "Refunds arrive in May of the year after each contribution year. Included so the goal-date net" +
                            " reflects what you're entitled to, not just what's already in your account.",
            )
        }

        val averageContribution = if (inputs.monthsToGoal > 0) totalContributions / inputs.monthsToGoal else 0.0
        return VehicleProjection(
            vehicle = VehicleType.FHSA,
            eligible = true,
            recommendation = Recommendation.BACKUP,
            recommendationReason = "FHSA combines RRSP-style deduction with TFSA-style withdrawal for a first home.",
            points = points,
            events = events,
            finalNetToUser = (points.lastOrNull()?.netToUser ?: 0.0) + pendingRefund,
            effectiveSavingsRateContribution = SavingsRateBreakdownCalculator.compute(
                monthlyContribution = averageContribution,
                vehicle = VehicleType.FHSA,
                taxContext = inputs.taxContext,
            ),
        )
    }

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    private fun monthlyFromAnnualReturn(annual: Double): Double = (1.0 + annual).pow(1.0 / 12.0) - 1.0

    /**
     * Sum of marginal-rate refunds for any contribution-year whose May refund
     * payout falls AFTER [goalDate]. The user has contributed but hasn't yet
     * received the refund at the goal date; the engine adds it to the
     * goal-date net so a short-term goal doesn't display RRSP/FHSA as worse
     * than TFSA just because the refund hasn't fired yet.
     */
    private fun computePendingRefund(
        yearlyContributions: Map<Int, Double>,
        goalDate: LocalDate,
        marginal: Double,
    ): Double = yearlyContributions.entries.sumOf { (year, amount) ->
        val refundDate = LocalDate(year + 1, Month.MAY, 1)
        if (refundDate > goalDate) amount * marginal else 0.0
    }

    /**
     * Annual new room added each calendar year, for [forYear]. Mixes two
     * sources:
     *   1. Hardcoded actuals from [rrspActualByYear] / [tfsaActualByYear]
     *      (update each November when CRA publishes the next year).
     *   2. CPI-projected values for any year past the latest known. TFSA
     *      projection rounds to the nearest $500 to match CRA's indexing
     *      rule. RRSP projects the money-purchase limit linearly forward.
     */
    fun annualRoomAccrual(vehicle: VehicleType, forYear: Int, taxContext: UserTaxContext): Double = when (vehicle) {
        VehicleType.RRSP -> {
            val annualMax = rrspAnnualMaxFor(forYear)
            minOf(taxContext.grossAnnualIncome * 0.18, annualMax).coerceAtLeast(0.0)
        }
        VehicleType.TFSA -> tfsaAnnualRoomFor(forYear)
        VehicleType.FHSA -> 8_000.0  // legislatively fixed; lifetime cap of $40k
        else -> 0.0
    }

    /** Convenience overload — projects using the user's profile tax year.
     *  Used by [StudioOrchestrator.roomCapMonthly] and any caller that
     *  only cares about the user's current year, not per-year accrual. */
    fun annualRoomAccrual(vehicle: VehicleType, taxContext: UserTaxContext): Double =
        annualRoomAccrual(vehicle, taxContext.taxYear, taxContext)

    /**
     * RRSP money-purchase annual maximum (the cap on the 18%-of-income
     * formula). Update when CRA publishes a new year each fall.
     */
    private val rrspActualByYear = mapOf(
        2024 to 31_560.0,
        2025 to 32_490.0,
        // ↑ Add new actuals here each November when CRA publishes.
    )

    /** TFSA annual contribution room. CRA usually publishes in late fall. */
    private val tfsaActualByYear = mapOf(
        2023 to 6_500.0,
        2024 to 7_000.0,
        2025 to 7_000.0,
        // ↑ Add new actuals here each November when CRA publishes.
    )

    /** Approximate CPI used to project unknown future years. The Bank of
     *  Canada's target is 2%, so we use that as a defensible default. A
     *  follow-up could pull live values from the Statistics Canada CPI
     *  API to drift this number with reality. */
    private val cpiProjection = 0.02

    private fun rrspAnnualMaxFor(year: Int): Double {
        rrspActualByYear[year]?.let { return it }
        val latestYear = rrspActualByYear.keys.max()
        if (year < rrspActualByYear.keys.min()) return rrspActualByYear.getValue(rrspActualByYear.keys.min())
        val drift = (1.0 + cpiProjection).pow((year - latestYear).toDouble())
        return rrspActualByYear.getValue(latestYear) * drift
    }

    private fun tfsaAnnualRoomFor(year: Int): Double {
        tfsaActualByYear[year]?.let { return it }
        val latestYear = tfsaActualByYear.keys.max()
        if (year < tfsaActualByYear.keys.min()) return tfsaActualByYear.getValue(tfsaActualByYear.keys.min())
        val drift = (1.0 + cpiProjection).pow((year - latestYear).toDouble())
        val projected = tfsaActualByYear.getValue(latestYear) * drift
        // CRA rounds the TFSA dollar limit to the nearest $500.
        val rounded = kotlin.math.round(projected / 500.0) * 500.0
        return rounded.coerceAtLeast(tfsaActualByYear.getValue(latestYear))
    }

    private fun ineligible(inputs: ProjectionInputs, reason: String): VehicleProjection = VehicleProjection(
        vehicle = inputs.vehicle,
        eligible = false,
        eligibilityReason = reason,
        recommendation = Recommendation.INELIGIBLE,
        recommendationReason = reason,
        points = emptyList(),
        events = emptyList(),
        finalNetToUser = 0.0,
        effectiveSavingsRateContribution = SavingsRateBreakdown(0.0, 0.0, 0.0),
    )

    private fun formatMoneyShort(amount: Double): String = kotlin.math.round(amount).toLong().toString()
    private fun formatPercent(rate: Double): String = "${kotlin.math.round(rate * 1000) / 10.0}%"
}
